import { useCallback, useState } from "react";

import { Group, Stack } from "@mantine/core";

import { SectionHeaderRefresh } from "@/components/SectionHeaderRefresh";
import Tabs, { ScrollingTabsList } from "@/components/Tabs";
import { CptCopyrightNotice } from "@/shared/cpt-copyright";
import { CptCodesHelpText } from "@/shared/procedure-codes";

import { REFRESH_BILLING_TABLES_LABEL } from "./constants/fee-table";
import CustomCodes from "./CustomCodes/CustomCodes";
import NonCustomFees from "./NonCustomFees";
import { FeeTableTab } from "./types/fee-table";

interface FeeTableProps {
  readOnly?: boolean;
}

/**
 * Fee Schedule section of the billing settings page: the CPT® codes and the practice's custom
 * codes, sharing one refresh control.
 */
export default function FeeTable({ readOnly = false }: FeeTableProps) {
  const [activeTab, setActiveTab] = useState<FeeTableTab>("non-custom");
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  const [nonCustomLoading, setNonCustomLoading] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);

  const isBillingLoading = nonCustomLoading || customLoading;

  const handleRefreshBilling = useCallback(() => {
    setBillingRefreshKey((previous) => previous + 1);
  }, []);

  const handleNonCustomLoading = useCallback((loading: boolean) => {
    setNonCustomLoading(loading);
  }, []);

  const handleCustomLoading = useCallback((loading: boolean) => {
    setCustomLoading(loading);
  }, []);

  return (
    <Tabs
      value={activeTab}
      styleVariant="underlined"
      data-testid="fee-table-tabs"
      onChange={(value) => setActiveTab(value as FeeTableTab)}
    >
      {/* The refresh control shares this row with the tab list, per Figma. */}
      <Group wrap="nowrap" align="center" justify="space-between">
        <ScrollingTabsList>
          <Tabs.Tab value="non-custom" data-testid="fee-table-tab-fee-schedule">
            CPT®
          </Tabs.Tab>
          <Tabs.Tab value="custom" data-testid="fee-table-tab-custom">
            Custom
          </Tabs.Tab>
        </ScrollingTabsList>
        <SectionHeaderRefresh
          sectionLabel="billing tables"
          isRefreshing={isBillingLoading}
          onRefresh={handleRefreshBilling}
          tooltipLabel={REFRESH_BILLING_TABLES_LABEL}
        />
      </Group>

      <Tabs.Panel pt="lg" keepMounted value="non-custom">
        <Stack gap="lg">
          <CptCopyrightNotice />
          <CptCodesHelpText context="settings" />
          <NonCustomFees
            readOnly={readOnly}
            refreshKey={billingRefreshKey}
            onLoadingChange={handleNonCustomLoading}
          />
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel pt="lg" keepMounted value="custom">
        <CustomCodes
          readOnly={readOnly}
          refreshKey={billingRefreshKey}
          onLoadingChange={handleCustomLoading}
        />
      </Tabs.Panel>
    </Tabs>
  );
}
